/**
 * Vax Schedule Firestore (booklet-driven model).
 * Collection: babies/{babyId}/vaxSchedule/{recordId}
 *
 * Single source of truth for both upcoming and given vaccines.
 * Replaces the older babies/{babyId}/vaccines + vaccinePrefs/schedule pair.
 */

import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc,
  query, orderBy, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type { VaxScheduleEntry } from '@/types';

function vaxCol(babyId: string) {
  return collection(db, 'babies', babyId, 'vaxSchedule');
}

/** Strip undefined keys — Firestore rejects undefined inside nested objects. */
function clean<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    if (v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
      out[k] = clean(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export async function getVaxSchedule(babyId: string): Promise<VaxScheduleEntry[]> {
  const snap = await getDocs(query(vaxCol(babyId), orderBy('ageOffsetDays', 'asc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as VaxScheduleEntry));
}

export async function addVaxEntry(
  babyId: string,
  entry: Omit<VaxScheduleEntry, 'id'>
): Promise<VaxScheduleEntry> {
  const ref = await addDoc(vaxCol(babyId), {
    ...clean(entry as unknown as Record<string, unknown>),
    createdAt: serverTimestamp(),
  });
  return { id: ref.id, ...entry };
}

export async function batchAddVaxEntries(
  babyId: string,
  entries: Omit<VaxScheduleEntry, 'id'>[]
): Promise<void> {
  if (entries.length === 0) return;
  const col = vaxCol(babyId);
  const batch = writeBatch(db);
  for (const e of entries) {
    const ref = doc(col);
    batch.set(ref, { ...clean(e as unknown as Record<string, unknown>), createdAt: serverTimestamp() });
  }
  await batch.commit();
}

export async function updateVaxEntry(
  babyId: string,
  entryId: string,
  patch: Partial<Omit<VaxScheduleEntry, 'id'>>
): Promise<void> {
  await updateDoc(doc(vaxCol(babyId), entryId), clean(patch as unknown as Record<string, unknown>));
}

export async function deleteVaxEntry(babyId: string, entryId: string): Promise<void> {
  await deleteDoc(doc(vaxCol(babyId), entryId));
}

/**
 * Merge incoming entries into existing schedule. Match by composite key
 * (vaccineName + doseNumber + ageBucket). Updates existing rows, adds new ones.
 *
 * Update rules:
 *   - Existing 'given' rows are NEVER downgraded to 'scheduled'.
 *   - Brand/batch/expiry/manufacturer/formulation: incoming wins if non-empty.
 *   - dueDateOverride: incoming wins if set.
 *   - Status promotion: scheduled → given is allowed; given → skipped requires explicit doctor source.
 */
export async function mergeVaxEntries(
  babyId: string,
  incoming: Omit<VaxScheduleEntry, 'id'>[]
): Promise<{ added: number; updated: number }> {
  const existing = await getVaxSchedule(babyId);
  const keyOf = (e: { vaccineName: string; doseNumber: number; ageBucket: string }) =>
    `${e.vaccineName.trim().toLowerCase()}|${e.doseNumber}|${e.ageBucket.trim().toLowerCase()}`;
  const byKey = new Map(existing.map(e => [keyOf(e), e]));

  const toAdd: Omit<VaxScheduleEntry, 'id'>[] = [];
  const updateOps: Promise<void>[] = [];

  for (const inc of incoming) {
    const match = byKey.get(keyOf(inc));
    if (!match) { toAdd.push(inc); continue; }

    const patch: Partial<Omit<VaxScheduleEntry, 'id'>> = {};
    // Status: only promote scheduled → given; preserve existing if already given.
    if (match.status !== 'given' && inc.status === 'given') {
      patch.status = 'given';
      if (inc.givenDate) patch.givenDate = inc.givenDate;
    } else if (inc.status === 'skipped' && (inc.addedBy === 'doctor' || inc.addedBy === 'manual')) {
      patch.status = 'skipped';
    } else if (inc.givenDate && match.status === 'given' && match.givenDate !== inc.givenDate) {
      patch.givenDate = inc.givenDate;
    }
    // Detail fields: incoming wins if non-empty AND new value
    (['brand', 'manufacturer', 'batchNumber', 'expiry', 'formulation', 'notes'] as const).forEach(k => {
      const v = inc[k];
      if (v && v !== match[k]) patch[k] = v;
    });
    if (inc.dueDateOverride && inc.dueDateOverride !== match.dueDateOverride) {
      patch.dueDateOverride = inc.dueDateOverride;
    }

    if (Object.keys(patch).length > 0) {
      updateOps.push(updateVaxEntry(babyId, match.id, patch));
    }
  }

  await Promise.all([
    toAdd.length > 0 ? batchAddVaxEntries(babyId, toAdd) : Promise.resolve(),
    ...updateOps,
  ]);

  return { added: toAdd.length, updated: updateOps.length };
}

/** Wipe the entire schedule — used when user re-uploads booklet from scratch. */
export async function clearVaxSchedule(babyId: string): Promise<void> {
  const snap = await getDocs(vaxCol(babyId));
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}
